import { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts'
import AuthContext from 'contexte/AuthContext'
import { simulateFees } from 'services/feesServices'
import { PRODUCTS, VARIABLE_LABELS, SUBSCRIPTION_LABELS, MARKETS, PAYMENT_METHODS, SIM_PRODUCTS, resultRows, totalFees, CURVE_AMOUNTS } from './feesConfig'
import { formatEur, formatPct, formatVariable } from './format'

const ERROR_LABELS = {
  unknown_fee_variable: 'Variable inconnue',
  invalid_fee_value: 'Valeur invalide pour',
  invalid_profile: 'Profil invalide',
  fees_service_unavailable: 'Le fees-manager ne répond pas',
  fees_service_not_configured: 'FEES_SERVICE_URL n’est pas configuré sur l’API admin'
}

const errorMessage = (code = '') => {
  const [key, detail] = code.split(':')
  return `${ERROR_LABELS[key] || code}${detail ? ` ${VARIABLE_LABELS[detail] || detail}` : ''}`
}

// Simulateur : chaque montant est calculé par le fees-manager avec ses vrais handlers, pour un client simulé,
// avec la grille actuelle et (si des valeurs sont modifiées) avec la grille modifiée.
const FeesSimulator = ({ schedule }) => {
  const { t } = useTranslation()
  const { user } = useContext(AuthContext)

  const [productKey, setProductKey] = useState('buy')
  const [options, setOptions] = useState({ market: 'eu_exante', price: 100, payment: 'wallet', isEtf: false, leverage: 5, orders: 50 })
  const [amount, setAmount] = useState(1000)
  const [profile, setProfile] = useState({ subscription: '', promotions: false, free_trade: false, partner_pct: 0, sales_pct: 0 })
  const [overrides, setOverrides] = useState({})
  const [openGroups, setOpenGroups] = useState({})
  const [inPercent, setInPercent] = useState(false)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const product = SIM_PRODUCTS[productKey]
  const current = useMemo(() => Object.fromEntries(schedule.variables.map(v => [v.name, v.value ?? ''])), [schedule])
  const changed = Object.fromEntries(Object.entries(overrides).filter(([name, value]) => String(value).trim() !== String(current[name] ?? '').trim()))
  const changedCount = Object.keys(changed).length

  const setOption = (key, value) => setOptions(o => ({ ...o, [key]: value }))
  const selectProduct = (key) => {
    setProductKey(key)
    const markets = SIM_PRODUCTS[key].markets
    if (markets && !markets.includes(options.market)) setOptions(o => ({ ...o, market: markets[0], price: MARKETS[markets[0]].price }))
    setResult(null)
  }
  const selectMarket = (key) => setOptions(o => ({ ...o, market: key, price: MARKETS[key].price }))

  const run = async () => {
    setLoading(true)
    setError(null)
    try {
      const amounts = [Number(amount), ...CURVE_AMOUNTS]
      const payloads = amounts.map(a => product.build(a, options))
      const simProfile = {
        ...profile,
        subscription: profile.subscription || null,
        partner_pct: Number(profile.partner_pct) || 0,
        sales_pct: Number(profile.sales_pct) || 0,
        card: PAYMENT_METHODS[options.payment].card
      }
      const results = await simulateFees(user, product.type, payloads, changed, simProfile)
      setResult({
        detail: results[0],
        curve: CURVE_AMOUNTS.map((a, i) => {
          const r = results[i + 1]
          const cur = totalFees(r.current), sim = r.simulated ? totalFees(r.simulated) : null
          return {
            amount: a,
            current: inPercent ? (cur / a) * 100 : cur,
            simulated: sim === null ? null : inPercent ? (sim / a) * 100 : sim,
            current_eur: cur, simulated_eur: sim
          }
        }),
        hasSimulation: Boolean(results[0].simulated)
      })
    } catch (e) {
      console.error(e)
      setError(e.response ? (e.response.data?.error || 'fees_service_unavailable') : e.message)
    } finally {
      setLoading(false)
    }
  }

  const togglePercent = () => {
    const next = !inPercent
    setInPercent(next)
    if (result) setResult(r => ({
      ...r,
      curve: r.curve.map(p => ({
        ...p,
        current: next ? (p.current_eur / p.amount) * 100 : p.current_eur,
        simulated: p.simulated_eur === null ? null : next ? (p.simulated_eur / p.amount) * 100 : p.simulated_eur
      }))
    }))
  }

  return (
    <div className='fees_sim'>
      <div className='fees_sim_inputs'>
        <section className='fees_card'>
          <h2>{t('pages.fees.sim.order')}</h2>
          <label className='fees_field'>
            <span>{t('pages.fees.sim.product')}</span>
            <select value={productKey} onChange={e => selectProduct(e.target.value)}>
              {Object.entries(SIM_PRODUCTS).map(([key, p]) => <option key={key} value={key}>{p.label}</option>)}
            </select>
          </label>
          <label className='fees_field'>
            <span>{t('pages.fees.sim.amount')}</span>
            <input type='number' min='1' step='any' value={amount} onChange={e => setAmount(e.target.value)} />
          </label>
          {product.markets && (
            <>
              <label className='fees_field'>
                <span>{t('pages.fees.sim.market')}</span>
                <select value={options.market} onChange={e => selectMarket(e.target.value)}>
                  {product.markets.map(key => <option key={key} value={key}>{MARKETS[key].label}</option>)}
                </select>
              </label>
              {!MARKETS[options.market].is_forex && (
                <label className='fees_field'>
                  <span>{t('pages.fees.sim.price')}</span>
                  <input type='number' min='0.01' step='any' value={options.price} onChange={e => setOption('price', Number(e.target.value))} />
                </label>
              )}
            </>
          )}
          {product.leverage && (
            <label className='fees_field'>
              <span>{t('pages.fees.sim.leverage')}</span>
              <input type='number' min='1' step='1' value={options.leverage} onChange={e => setOption('leverage', Number(e.target.value))} />
            </label>
          )}
          {product.payment && (
            <label className='fees_field'>
              <span>{t('pages.fees.sim.payment')}</span>
              <select value={options.payment} onChange={e => setOption('payment', e.target.value)}>
                {Object.entries(PAYMENT_METHODS).map(([key, p]) => <option key={key} value={key}>{p.label}</option>)}
              </select>
            </label>
          )}
          {product.etf && (
            <label className='fees_check'>
              <input type='checkbox' checked={options.isEtf} onChange={e => setOption('isEtf', e.target.checked)} />
              <span>{t('pages.fees.sim.etf')}</span>
            </label>
          )}
          {product.copy && (
            <label className='fees_field'>
              <span>{t('pages.fees.sim.orders')}</span>
              <input type='number' min='1' step='1' value={options.orders} onChange={e => setOption('orders', Math.max(1, Number(e.target.value)))} />
              <small>{t('pages.fees.sim.orders_help')}</small>
            </label>
          )}
        </section>

        <section className='fees_card'>
          <h2>{t('pages.fees.sim.client')}</h2>
          <label className='fees_field'>
            <span>{t('pages.fees.sim.subscription')}</span>
            <select value={profile.subscription} onChange={e => setProfile(p => ({ ...p, subscription: e.target.value }))}>
              <option value=''>{t('pages.fees.sim.no_subscription')}</option>
              {schedule.subscriptions.map(s => (
                <option key={s.subscription_type} value={s.subscription_type}>
                  {SUBSCRIPTION_LABELS[s.subscription_type] || s.subscription_type} ({formatPct(s.discount, 2)})
                </option>
              ))}
            </select>
          </label>
          <label className='fees_check'>
            <input type='checkbox' checked={profile.promotions} onChange={e => setProfile(p => ({ ...p, promotions: e.target.checked }))} />
            <span>{t('pages.fees.sim.promotions')}</span>
          </label>
          <label className='fees_check'>
            <input type='checkbox' checked={profile.free_trade} onChange={e => setProfile(p => ({ ...p, free_trade: e.target.checked }))} />
            <span>{t('pages.fees.sim.free_trade')}</span>
          </label>
          <div className='fees_inline'>
            <label className='fees_field'>
              <span>{t('pages.fees.sim.partner_pct')}</span>
              <input type='number' min='0' max='100' step='any' value={profile.partner_pct} onChange={e => setProfile(p => ({ ...p, partner_pct: e.target.value }))} />
            </label>
            <label className='fees_field'>
              <span>{t('pages.fees.sim.sales_pct')}</span>
              <input type='number' min='0' max='100' step='any' value={profile.sales_pct} onChange={e => setProfile(p => ({ ...p, sales_pct: e.target.value }))} />
            </label>
          </div>
        </section>

        <section className='fees_card'>
          <div className='fees_card_title'>
            <h2>{t('pages.fees.sim.overrides')}</h2>
            {changedCount > 0 && <button className='fees_link_button' onClick={() => setOverrides({})}>{t('pages.fees.sim.reset')} ({changedCount})</button>}
          </div>
          <p className='fees_note'>{t('pages.fees.sim.overrides_help')}</p>
          {PRODUCTS.map(group => {
            const vars = schedule.variables.filter(v => v.product === group.key)
            const groupChanged = vars.filter(v => changed[v.name] !== undefined).length
            return (
              <div key={group.key} className='fees_group'>
                <button className='fees_group_title' onClick={() => setOpenGroups(g => ({ ...g, [group.key]: !g[group.key] }))}>
                  <span>{openGroups[group.key] ? '▾' : '▸'} {group.title}</span>
                  {groupChanged > 0 && <span className='fees_badge'>{groupChanged}</span>}
                </button>
                {openGroups[group.key] && vars.map(v => (
                  <label key={v.name} className={`fees_override ${changed[v.name] !== undefined ? 'fees_override_changed' : ''}`}>
                    <span>{VARIABLE_LABELS[v.name] || v.name}<small>{t('pages.fees.sim.current')} : {formatVariable(v)}</small></span>
                    {v.kind === 'boolean' ? (
                      <select value={overrides[v.name] ?? current[v.name]} onChange={e => setOverrides(o => ({ ...o, [v.name]: e.target.value }))}>
                        <option value='true'>Oui</option>
                        <option value='false'>Non</option>
                      </select>
                    ) : (
                      <input type='text' inputMode='decimal' value={overrides[v.name] ?? current[v.name]}
                        onChange={e => setOverrides(o => ({ ...o, [v.name]: e.target.value.replace(/\s/g, '') }))} />
                    )}
                  </label>
                ))}
              </div>
            )
          })}
        </section>

        <button className='fees_run' onClick={run} disabled={loading || !(Number(amount) > 0)}>
          {loading ? t('pages.fees.sim.running') : t('pages.fees.sim.run')}
        </button>
        {error && <div className='fees_error'>{errorMessage(error)}</div>}
      </div>

      <div className='fees_sim_results'>
        {!result && <div className='fees_card fees_empty'>{t('pages.fees.sim.empty')}</div>}
        {result && <ResultTable result={result} amount={Number(amount)} t={t} />}
        {result && (
          <section className='fees_card'>
            <div className='fees_card_title'>
              <h2>{t('pages.fees.sim.curve')}</h2>
              <label className='fees_check'>
                <input type='checkbox' checked={inPercent} onChange={togglePercent} />
                <span>{t('pages.fees.sim.in_percent')}</span>
              </label>
            </div>
            <div className='fees_chart'>
              <ResponsiveContainer width='100%' height={300}>
                <LineChart data={result.curve} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                  <CartesianGrid stroke='var(--border)' strokeDasharray='3 3' />
                  <XAxis dataKey='amount' tickFormatter={a => formatEur(a, 0)} stroke='var(--text-secondary)' fontSize={12} />
                  <YAxis tickFormatter={v => inPercent ? formatPct(v, 2) : formatEur(v, 0)} stroke='var(--text-secondary)' fontSize={12} width={70} />
                  <Tooltip formatter={v => inPercent ? formatPct(v, 3) : formatEur(v)} labelFormatter={a => `${t('pages.fees.sim.amount')} : ${formatEur(a, 0)}`}
                    contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }} />
                  <Legend />
                  <Line type='monotone' dataKey='current' name={t('pages.fees.sim.col_current')} stroke='var(--text-secondary)' strokeWidth={2} dot={false} />
                  {result.hasSimulation && <Line type='monotone' dataKey='simulated' name={t('pages.fees.sim.col_simulated')} stroke='var(--primary)' strokeWidth={2} dot={false} />}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}
      </div>
    </div>
  )
}

// Détail du calcul pour le montant saisi : actuel, modifié, écart
const ResultTable = ({ result, amount, t }) => {
  const cur = resultRows(result.detail.current)
  const sim = result.detail.simulated ? resultRows(result.detail.simulated) : null
  const keys = [...new Set([...cur.map(r => r.key), ...(sim || []).map(r => r.key)])]
  const row = (rows, key) => rows?.find(r => r.key === key)
  const fmt = (r) => !r ? '—' : `${r.minus && r.value ? '−' : ''}${formatEur(r.value)}`
  const total = row(cur, 'total')?.value
  return (
    <section className='fees_card'>
      <h2>{t('pages.fees.sim.detail')} ({formatEur(amount, 0)})</h2>
      <table className='fees_table fees_table_head fees_result'>
        <thead>
          <tr>
            <th></th>
            <th>{t('pages.fees.sim.col_current')}</th>
            {sim && <th>{t('pages.fees.sim.col_simulated')}</th>}
            {sim && <th>{t('pages.fees.sim.col_delta')}</th>}
          </tr>
        </thead>
        <tbody>
          {keys.map(key => {
            const c = row(cur, key), s = row(sim, key)
            const delta = s && c ? s.value - c.value : null
            return (
              <tr key={key} className={(c || s)?.strong ? 'fees_row_strong' : ''}>
                <td>{(c || s).label}</td>
                <td className='fees_value'>{fmt(c)}</td>
                {sim && <td className='fees_value'>{fmt(s)}</td>}
                {sim && <td className={`fees_value ${delta > 0.004 ? 'fees_up' : delta < -0.004 ? 'fees_down' : ''}`}>
                  {delta === null || Math.abs(delta) < 0.005 ? '—' : `${delta > 0 ? '+' : '−'}${formatEur(Math.abs(delta))}`}
                </td>}
              </tr>
            )
          })}
        </tbody>
      </table>
      {total !== undefined && amount > 0 && <p className='fees_note'>{t('pages.fees.sim.share_of_amount')} : {formatPct((total / amount) * 100, 3)}</p>}
      {result.detail.current.free_trade && <p className='fees_note'>{t('pages.fees.sim.free_trade_applied')}</p>}
    </section>
  )
}

export default FeesSimulator
